/**
 * SelectorDeMoneda.tsx — la moneda se elige, no se teclea.
 *
 * ▶▶ ERA UN `<input maxLength={3}>` EN CINCO SITIOS.
 *
 * Ficha de contraparte, factura de compra, factura de venta, pago, y la moneda
 * local en `/configuracion` y en la consola. La única regla era la de la tabla
 * —`~ '^[A-Z]{3}$'`— que acepta `XYZ` y `EUR` exactamente igual: una no existe
 * y la otra existe pero este sistema no tiene su tipo de cambio.
 *
 * Y el error no se descubre donde se comete. Un proveedor con moneda `EUR` se
 * guarda sin una queja; lo que falla es la primera factura suya, semanas
 * después, con un mensaje sobre tipos de cambio que no menciona la ficha.
 *
 * ── Dos preguntas distintas, dos listas ───────────────────────────────────
 *
 *   `operable`  en qué monedas puede MOVERSE esta empresa — la local, que no
 *               se convierte, más las que tienen tipo publicado. Es la de una
 *               factura, un pago y la ficha de una contraparte.
 *
 *   `conocida`  qué monedas conoce el sistema — para elegir la moneda LOCAL de
 *               una empresa, donde la de hoy no es una respuesta y la lista de
 *               convertibles tampoco. La usa la consola del operador, que ni
 *               siquiera tiene una empresa en contexto todavía.
 *
 * Las dos las arma la base (0094 y 0096). Escribir cualquiera de las dos acá
 * sería una lista que envejece cada vez que el hub replica una moneda nueva.
 */
import { useIdioma } from '../nucleo/idioma.tsx';
import { useMonedas } from '../nucleo/monedas.tsx';

interface Props {
  readonly valor: string;
  readonly alCambiar: (moneda: string) => void;
  /** Cuál de las dos preguntas. Ver arriba. */
  readonly lista?: 'operable' | 'conocida';
  /** Qué se lee cuando no hay ninguna elegida. */
  readonly vacio: string;
  readonly deshabilitado?: boolean;
  readonly id?: string;
}

export function SelectorDeMoneda({
  valor, alCambiar, lista = 'operable', vacio, deshabilitado = false, id,
}: Props) {
  const { t } = useIdioma();
  const { operables, conocidas } = useMonedas();
  const filas = lista === 'operable' ? operables : conocidas;

  /* ▶ La elegida entra en la lista aunque no esté.
     Una ficha guardada hace meses puede tener una moneda que hoy ya no sale —
     el hub dejó de publicar su tipo. Sin esta línea, abrir esa ficha mostraría
     el desplegable en «sin declarar» y GUARDARLA le borraría la moneda a un
     documento que sí la tenía. Un campo no puede perder un dato por el hecho
     de mirarlo. */
  const codigos = filas.map((f) => f.moneda);
  const todas = valor !== '' && !codigos.includes(valor) ? [valor, ...codigos] : codigos;

  return (
    <select
      id={id} className="select" value={valor} disabled={deshabilitado}
      onChange={(e) => alCambiar(e.target.value)}
    >
      <option value="">{vacio}</option>
      {todas.map((m) => {
        const f = filas.find((x) => x.moneda === m);
        return (
          <option key={m} value={m}>
            {m}
            {f && 'es_local' in f && f.es_local ? ` · ${t('cp.currencyLocal')}` : ''}
            {!f ? ` · ${t('cur.gone')}` : ''}
          </option>
        );
      })}
    </select>
  );
}
